import Utility from 'shared_modules/Utility';
import $ from 'jquery';
import axios from 'axios';
import get from 'lodash/get';
import find from 'lodash/find';
import intersection from 'lodash/intersection';
import Monitoring from 'shared_modules/Monitoring';
import Terminal from 'app/src/payments/terminals/Terminal';

// Version doesn't have much context right now with Retail.  Perhaps, once a version is developed we
// can change this to pull from the version source.
const _productVersion = '1.0';

const STRIPE_SDK_FILENAME = ''; // Only specified if using a version different from the official release
const STRIPE_SDK_VERSION = '/terminal/v1/';
const STRIPE_CDN_URL = 'https://js.stripe.com';
const CTS_TOKEN_URL = '/admin/payments/CtsJwt';
const STRIPE_SUPPORTED_COUNTRY_CODES = [
  { countryCode: 'CA', iso3: 'can' },
  { countryCode: 'US', iso3: 'usa' },
];

const _TransactionStatus = {
  APPROVED: 'APPROVED',
  CANCELLED: 'CANCELLED',
  REVERSED: 'REVERSED',
  DECLINED: 'DECLINED',
  SYSTEM_ERROR: 'SYSTEM ERROR',
  NOT_STARTED: 'NOT STARTED',
  UNKNOWN: 'UNKNOWN',
};

export const UnhandledStripeServiceError = {
  NO_ESTABLISHED_CONNECTION: 'no_established_connection', // The command failed because no reader is connected.
  NO_ACTIVE_COLLECT_PAYMENT_METHOD_ATTEMPT: 'no_active_collect_payment_method_attempt', // cancelCollectPaymentMethod can only be called when collectPaymentMethod is in progress.
  NO_ACTIVE_READ_REUSABLE_CARD_ATTEMPT: 'no_active_read_reusable_card_attempt', // cancelCollectReusableCard can only be called when readReusableCard is in progress.
  CANCELED: 'canceled', // The command was canceled from POS
  CANCELABLE_ALREADY_COMPLETED: 'cancelable_already_completed', // Cancelation failed because the operation has already completed.
  CANCELABLE_ALREADY_CANCELED: 'cancelable_already_canceled', // Cancelation failed because the operation has already been canceled.
  NETWORK_ERROR: 'network_error', // An unknown error occurred when communicating with the server or reader over the network. Refer to the error message for more information.
  NETWORK_TIMEOUT: 'network_timeout', // The request timed out when communicating with the server or reader over the network. Make sure both your device and the reader are connected to the network with stable connections.
  ALREADY_CONNECTED: 'already_connected', // connectReader failed because a reader is already connected.
  FAILED_FETCH_CONNECTION_TOKEN: 'failed_fetch_connection_token', // Failed to fetch a connection token. Make sure your connection token handler returns a promise that resolves to the connection token.
  DISCOVERY_TOO_MANY_READERS: 'discovery_too_many_readers', // discoverReaders returned too many readers. Use Locations to filter discovered readers by location.
  INVALID_READER_VERSION: 'invalid_reader_version', // The reader is running an unsupported software version. Please allow the reader to update and try again.
  READER_ERROR: 'reader_error', // The reader returned an error while processing the request. Refer to the error message for more information. This is also reached when customer cancels payment on terminal.
  PAYMENT_INTENT_UNEXPECTED_STATE: 'payment_intent_unexpected_state', // The reader return an error while processing a payment.
  PAYMENT_WAIT_INPUT: 'waiting_for_input', // The reader internal status waiting for the user input.
  // Non-documented API error codes that are not reaching the bank network and by the same time have no webhooks in case of failure.
  EXPIRED_CARD: 'expired_card', // The card has expired. Check the expiration date or use a different card.
  INVALID_EXPIRY_MONTH: 'invalid_expiry_month', // The card’s expiration month is incorrect. Check the expiration date or use a different card.
  INVALID_EXPIRY_YEAR: 'invalid_expiry_year', // The card’s expiration year is incorrect. Check the expiration date or use a different card.

  // Catch all custom error codes
  READER_REGISTER_ERROR: 'reader_register_error',
  CONFIGURATION_EXCEPTION: 'configuration_exception', // If one of the SDK method fail or the merchant has bad configuration
  SERVICE_EXCEPTION: 'service_exception', // If one of the SDK method fail or the merchant has bad configuration
  UNKNOWN_CODE: 'unknown_code', // If one of the error returns no error code.
};

export const StripeTerminalPaymentStatus = {
  WAITING_FOR_INPUT: 'waiting_for_input', // Waiting for payment from user
  NOT_READY: 'not_ready',
  READY: 'ready', // Ready to accept a payment
  PROCESSING: 'processing', // Processing Payment
};

export default class StripeTerminalService {
  static instance;

  /**
   * Retrieves the singleton instance of the StripeTerminalService. This is constructed as
   * a singleton because we can only have one instance of the Stripe API active at once.
   */
  static getInstance() {
    let deferred = $.Deferred();

    if (StripeTerminalService.instance) {
      deferred.resolve(StripeTerminalService.instance);
    } else {
      const countryCode = get(window.merchantos, 'locale.country_code');
      const currency = get(window.merchantos, 'locale.currency_code');
      const supportedCountryCode = find(STRIPE_SUPPORTED_COUNTRY_CODES, {
        countryCode,
      });

      if (!currency && typeof currency !== 'string') {
        deferred.reject({
          code: UnhandledStripeServiceError.CONFIGURATION_EXCEPTION,
          message: `Unable to connect to your terminal. This could be caused by an incompatible configuration setting or an unsupported currency set for this account.\n You can update this under Settings > General Options.`,
          reason: `Currency code value is: ${currency}`,
        });
      } else if (!supportedCountryCode) {
        deferred.reject({
          code: UnhandledStripeServiceError.CONFIGURATION_EXCEPTION,
          message: `Unable to connect to your terminal. This could be caused by an incompatible configuration setting or an unsupported account location.\n You can update this under Settings > General Options.`,
          reason: `Country code value is: ${countryCode}`,
        });
      } else {
        const stripeTerminalSDKDeferred = $.Deferred();

        Utility.fetchScript(`${STRIPE_CDN_URL}${STRIPE_SDK_VERSION}${STRIPE_SDK_FILENAME}`)
          .done(() => {
            stripeTerminalSDKDeferred.resolve();
          })
          .fail(e => {
            stripeTerminalSDKDeferred.reject(e);
          });

        $.when(stripeTerminalSDKDeferred)
          .done(_stripeTerminalSDK => {
            try {
              StripeTerminalService.instance = new StripeTerminalService(
                supportedCountryCode,
                currency
              );
              deferred.resolve(StripeTerminalService.instance);
            } catch (e) {
              deferred.reject(e);
            }
          })
          .fail(e => {
            deferred.reject(e);
          });
      }
    }

    return deferred.promise();
  }

  static get TransactionStatus() {
    return _TransactionStatus;
  }

  /**
   * Constructs a StripeTerminalService instance
   *
   * @returns A Promise of the StripeTerminalService
   */
  constructor(supportedCountryCode, currency) {
    this.currency = currency;

    this._stripeTerminalApi = window.StripeTerminal.create({
      onFetchConnectionToken: async () => {
        try {
          // The following call needs to be performed by onFetchConnectionToken
          // since this method is called every time we do initiate the Stripe SDK
          // which will refresh JWT token expiration timer. The JWT expires every 10 min.
          const { data: { jwt: bearerToken, url: urlToken } = {} } = await axios.post(
            CTS_TOKEN_URL
          );

          const url = new URL(
            `/payments/stripe-terminal-token/${supportedCountryCode.iso3}`,
            urlToken
          );

          const { data: { secret: secretToken } = {} } = await axios.request({
            headers: { Authorization: `Bearer ${bearerToken}` },
            method: 'POST',
            url,
          });

          return secretToken;
        } catch (error) {
          throw error;
        }
      },
      onUnexpectedReaderDisconnect: () => {
        console.info('Terminal Event:', 'Unexpected Terminal Disconnect');
      },
      onConnectionStatusChange: ev => {
        console.info('Terminal Event:', ev.status);
      },
      // Allows the cancellation of the collect payment method from the terminal.
      allowCustomerCancel: true,
    });
  }

  /**
   * Begins discovering readers - Step 1
   *
   *
   * @returns a Promise that resolves to an object with the following fields:
   *
   * @return {object[]} discoveredReaders: A list of discovered Reader objects, if the command succeeded
   * @return {error} error: An error, if the command failed.
   */
  discoverReaders(gatewayParameters) {
    let deferred = $.Deferred();
    let processor, terminalLocation;

    try {
      const { supportedProcessors, processorData } = gatewayParameters || {};
      const supportedCountries = STRIPE_SUPPORTED_COUNTRY_CODES.map(({ iso3 }) => `stripe-${iso3}`);

      if (supportedProcessors) {
        processor = intersection(supportedCountries, JSON.parse(supportedProcessors))[0];
      } else {
        throw new Error('Unable to get supportedProcessors');
      }

      if (processorData) {
        terminalLocation = get(JSON.parse(processorData), `${processor}.terminalLocation`, null);
      } else {
        throw new Error('Unable to get processorData');
      }

      this._stripeTerminalApi
        .discoverReaders({
          method: null,
          location: terminalLocation,
        })
        .then(response => {
          this._handleStripeResponse(response, deferred);
        })
        .catch(exception => {
          this._handleException(exception);

          deferred.reject({
            code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
            message: 'Unable to discover readers',
          });
        });
    } catch ({ message }) {
      deferred.reject({
        code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
        message,
      });
    }

    return deferred.promise();
  }

  /**
   * Attempts to connect to the given reader - Step 2
   *
   *
   * @param {object} selectedReader Represent the reader object
   * coming back from the discover reader response
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {object} reader: The connected Reader, if the command succeeded.
   * @return {error} error: An error, if the command failed.
   */
  connectReader(selectedReader) {
    let deferred = $.Deferred();

    this._stripeTerminalApi
      .connectReader(selectedReader)
      .then(response => {
        this._handleStripeResponse(response, deferred);
      })
      .catch(exception => {
        this._handleException(exception);

        // But we still notify the user something went wrong with a "nicer" message
        deferred.reject({
          code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
          message: 'Unable to connect to a reader',
        });
      });

    return deferred.promise();
  }

  /**
   * Disconnects from the connected reader - Step 2a
   */
  disconnectReader() {
    this._stripeTerminalApi.disconnectReader();
  }

  /**
   * Get the connection status from the reader instance
   */
  getConnectionStatus() {
    return this._stripeTerminalApi.getConnectionStatus();
  }

  /**
   * Get the payment status from the reader instance
   * https://stripe.com/docs/terminal/js-api-reference#get-payment-status
   */
  getPaymentStatus() {
    return this._stripeTerminalApi.getPaymentStatus();
  }

  /**
   * Begins collecting a payment method for a PaymentIntent - Step 3
   *
   *
   * This method takes a single argument, the client_secret field
   * from a PaymentIntent object created on your backend.
   *
   * @param {string} paymentIntentClientSecret field from a PaymentIntent object created
   * by the backend.
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {object} paymentIntent: The updated PaymentIntent object, if the command succeeded
   * @return {error} error: An error, if the command failed.
   */
  collectPaymentMethod(paymentIntentClientSecret) {
    let deferred = $.Deferred();

    this._stripeTerminalApi
      .collectPaymentMethod(paymentIntentClientSecret)
      .then(response => {
        this._handleStripeResponse(response, deferred);
      })
      .catch(exception => {
        this._handleException(exception);

        // But we still notify the user something went wrong with a "nicer" message
        deferred.reject({
          code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
          message: 'Unable to collect the payment method',
        });
      });

    return deferred.promise();
  }

  /**
   * Cancels an outstanding collectPaymentMethod command on the Terminal.
   *
   * @returns a Promise that resolves to an empty object once the command has been successfully canceled.
   * @return {error} If cancellation fails, the Promise resolves to an object with an error
   */
  cancelCollectPaymentMethod() {
    let deferred = $.Deferred();

    this._stripeTerminalApi
      .cancelCollectPaymentMethod()
      .then(response => {
        this._handleStripeResponse(response, deferred);
      })
      .catch(exception => {
        this._handleException(exception);

        // But we still notify the user something went wrong with a "nicer" message
        deferred.reject({
          code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
          message: 'Unable to cancel the collect payment method',
        });
      });

    return deferred.promise();
  }

  /**
   * Processes a payment after a payment method has been collected - Step 4
   *
   *
   * This method takes a single parameter, a PaymentIntent object obtained
   * from a successful call to collectPaymentMethod.
   *
   * @param {object} paymentIntent object that tracks the state of the payment through
   * the status attribute. When the payment is successful, the status of the PaymentIntent
   * changes to succeeded and you can confidently fulfill the order.
   *
   * @returns  a Promise that resolves to an object with the following fields:
   * @return {object} paymentIntent: The confirmed PaymentIntent object, if the command succeeded.
   * @return {error} error: An error, if the command failed. For more information, see Handling processing failures.
   */
  processPaymentIntent(paymentIntent) {
    let deferred = $.Deferred();
    this._stripeTerminalApi
      .processPayment(paymentIntent)
      .then(response => {
        this._handleStripeResponse(response, deferred);
      })
      .catch(exception => {
        this._handleException(exception);

        // But we still notify the user something went wrong with a "nicer message"
        deferred.reject({
          code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
          message: 'Unable to process the payment intent',
        });
      });

    return deferred.promise();
  }

  /**
   * Begins collecting a payment method for an Interac Refund - Step 3.a
   *
   *
   * This method takes two arguments, the original chargeId
   * and the amount to be refunded on Interac.
   *
   * @param {string} chargeId: Original chargeId extracted from the refunded payment
   * @param {number} amount: Amount in cents you want to refund
   * this value should smaller or equal to the amount contained in the
   * original charge. This amount should be absolute.
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {} a empty deferred promise resolution
   * @return {error} a deferred promise rejection with an error message
   */

  collectRefundPaymentMethod(chargeId, amount) {
    let deferred = $.Deferred();

    // For more details on the following method please see:
    // https://stripe.com/docs/terminal/js-api-reference#stripeterminal-collectrefundpaymentmethod

    this._stripeTerminalApi
      .collectRefundPaymentMethod(chargeId, amount, this.currency.toLowerCase(), {
        reverse_transfer: true,
        refund_application_fee: false,
      })
      .then(response => {
        this._handleStripeResponse(response, deferred);
      })
      .catch(exception => {
        this._handleException(exception);

        deferred.reject({
          code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
          message: 'Unable to collect the refund method',
        });
      });

    return deferred.promise();
  }

  /**
   * Processes a refund after a payment refund method has been collected - Step 4.a
   *
   *
   * This method takes no parameter and needs to be executed
   * right after a successful call to collectRefundPaymentMethod.
   *
   * @returns  a Promise that resolves to an object with the following fields:
   * @return {} a empty deferred promise resolution
   * @return {error} a deferred promise rejection with an error message
   */

  processRefund() {
    let deferred = $.Deferred();

    this._stripeTerminalApi
      .processRefund()
      .then(response => {
        this._handleStripeResponse(response, deferred);
      })
      .catch(exception => {
        this._handleException(exception);

        deferred.reject({
          code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
          message: 'Unable to process the refund intent',
        });
      });

    return deferred.promise();
  }

  /**
   * Begins collecting a payment method for an unreferenced card refund
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {} a empty deferred promise resolution
   * @return {error} a deferred promise rejection with an error message
   */
  collectUnreferencedRefundPaymentMethod() {
    let deferred = $.Deferred();

    this._stripeTerminalApi
      .readReusableCard()
      .then(response => {
        this._handleStripeResponse(response, deferred);
      })
      .catch(exception => {
        this._handleException(exception);

        deferred.reject({
          code: UnhandledStripeServiceError.SERVICE_EXCEPTION,
          message: 'Unable to collect the refund method',
        });
      });

    return deferred.promise();
  }

  /**
   * This little handler make sure to return an error
   * or the response since Stripe always returns a
   * successful promise
   *
   * @param {Object} response
   * @param {JQuery.deferred} deferred
   */
  _handleStripeResponse(response, deferred) {
    const { error } = response;

    // If the response contains an error we throw back the error
    if (error) {
      // The following will resolve the error code with 'unknown_code' unless specified in the error
      deferred.reject({ code: UnhandledStripeServiceError.UNKNOWN_CODE, ...error });
    } else {
      deferred.resolve(response);
    }
  }

  /**
   * Throw to the console any exception
   *
   * @param {*} exception
   */
  _handleException(exception) {
    // We throw to the console the exception
    Monitoring.NRError(exception, 'StripeTerminalService', {
      errorCode: 'stripe_service_exception',
    });
    console.error(exception);
  }
}
